<template>
  <div class="canvas-container" :class="{roundedCorners: selectedProductIsRounded}">
    <div id="trimLine" :style="trimStyle" />
    <canvas ref="canvasEl"></canvas>

    <EditorControlsOverlay ref="editorControlsOverlay"
                           :fabricCanvas="this.fabricCanvas"
                           :bleedPoints="this.bleedPoints"
                           :displayBleed="displayBleed"
                           :bus="bus"
                           @fileData="placeholderImageDataUploaded"></EditorControlsOverlay>
  </div>
</template>

<style scoped lang="scss">
@import '~scss/colors';

.canvas-container {
  background: white;
  position: relative;
}
.roundedCorners #trimLine {
  border-radius: 18px;   // 1/8 inch = 9 pts = 18px
}
canvas {
  position: absolute;
  left: 0;
  top: 0;
}
</style>

<script>
import { fabric } from "fabric";
import Scaler from "@/lib/Scaler";
import TemplateVars from "@/lib/TemplateVars";
import UndoStack from "@/lib/UndoStack";
import initAligningGuidelines from "@/lib/initAligningGuidelines"
import fitRectIntoBounds from "@/lib/fitRectIntoBounds"
import centerRectWithinBounds from "@/lib/centerRectWithinBounds"
import selectTopLeftObject from "@/lib/selectTopLeftObject"
import cropCanvas from "@/lib/cropCanvas"
import getBleedPoints from "@/lib/getBleedPoints"
import anyBadBleed from "@/lib/anyBadBleed"
import { mapActions, mapGetters } from "vuex";
import EditorControlsOverlay from './EditorControlsOverlay'


import flatten from 'flatten'
import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';

const RATIO = 1.5;  // TODO where does this come from

//if there are <= this number of bad bleed points, we don't show them or put up the warning
const BLEED_POINT_THRESHOLD = 2;

export default {
  components:{
    EditorControlsOverlay
  },
  props: ['pageNum', 'fontsLoaded', 'bus', 'designLocked', 'nofill'],
  data() {
    return {
      fabricCanvas: null,
      loaded: false,
      currentObject: null,
      page: null,
      canvasState: {
        fonts: [],
        colors: [],
        fontName: '',
        fontSize: 0,
        color: '',
        position: {x:null, y:null},
        canUndo: false,
        canRedo: false,
        textSelected:false,
        originX: undefined,
        allowMultiline: undefined,
        textTransform: undefined,
        fieldLocked: undefined,
        fieldDeletable: undefined,
        variable: undefined,
        isPlaceholder: undefined,
        hasBadBleed:false,
        displayBleed:true,
        hasTextAtStartup:true,
        blockId:undefined,
        dismissedBleedWarning:false, //serialized via layers meta object
        blocks:null //serialized via layers meta object
      },
      trimHorizontalMargins: 0,
      trimVerticalMargins: 0,
      textMode:true,
      bleedPoints:[],
      forceDisplayBleed: null
    };
  },
  computed: {
    ...mapGetters(['design', 'selectedProductIsRounded', 'thumbnails', 'flags']),
    backgroundSvgMarkup() {
      return this.page && this.page.backgroundLayer.svgMarkup;
    },
    backgroundSvgDataUrl() {
      if (!this.backgroundSvgMarkup) return;
      let svg = this.backgroundSvgMarkup;
      return 'data:image/svg+xml;base64,'+btoa(svg);
    },
    trimStyle() {
      if ((this.trimHorizontalMargins < 0.1 && this.trimVerticalMargins < 0.1) && !this.selectedProductIsRounded){
        return {display: 'none'};
      };

      const style =  {
        position: 'absolute',
        top: 0, right: 0, bottom: 0, left: 0,
        margin: `${this.trimVerticalMargins}px ${this.trimHorizontalMargins}px`,
        border: 'dashed 1px cyan',
        zIndex: 1,
        pointerEvents: 'none'
      }
      if (!this.displayBleed){
        delete style.border;
        style.boxShadow = '0 0 0 30pt $gray-3';
      }
      return style;



    },
    thumbnail() {
      return this.thumbnails[this.pageNum];
    },
    designPage() {
      return this.design && this.design.pages[this.pageNum];
    },
    displayBleed() {
      // When filling out a design (is, there is a source design) we don't show bleed.
      // This can be forced by a toggle as well.
      if (this.forceDisplayBleed != null) return this.forceDisplayBleed;
      return !this.canvasState.dismissedBleedWarning && this.design && !this.design.sourceDesign;
    }
  },
  watch: {
    // When we have both fonts loaded and page for the design,
    // copy page data into $data and trigger canvas setup
    fontsLoaded(val) {
      this.$data.page = val && this.designPage;
    },
    designPage(val) {
      this.$data.page = this.fontsLoaded && val;
    },
    page(val) {
      console.log('watch page', val);
      if (val) this.loadPage();
    },


    // Thumbanil may disappear when design is modified by adding back file.
    // Recreate the thumbnail in this case.
    thumbnail(val, old) {
      if (this.page && old && !val) {
        this.updateThumbnail();
      }
    },

    // Copy current canvas object data over to where toolbar can access it.
    currentObject(obj) {
      this.updateCanvasState();
    },

    // Let parent component know the canvasState has been updated, in the Vue Way.
    canvasState: {
      deep: true,
      handler(canvasState, prevCanvasState) {
        this.$emit('input', {canvasState, pageNum: this.pageNum});
        if (!prevCanvasState.dismissedBleedWarning !== canvasState.dismissedBleedWarning ){
          this.save();
        }
      }
    }
  },
  mounted() {
    if (this.page) this.loadPage();
  },
  events: {
    undo() {
      this.undoStack.undo();
    },
    redo() {
      this.undoStack.redo();
    },
    convertToBlock(){
      const activeObject = this.fabricCanvas.getActiveObject();
      if (activeObject && activeObject.type === 'activeSelection') {
        this.updateCustomPropertiesOnMultipleObjects(activeObject.getObjects(), objWithinSelection => objWithinSelection.blockId = 1)
        this.canvasState.blocks = this.canvasState.blocks || {};
        this.canvasState.blocks[1] = {verticalAlign:'top'};
      }
      this.fabricCanvas.trigger('object:modified', {target: this.fabricCanvas.getActiveObject()});
      this.commitModification();
    },
    deleteBlock(blockIdToDelete) {
      this.fabricCanvas.getObjects().forEach(obj => {
        let {blockId} = obj.brandly || {};
        if (blockId === blockIdToDelete) {
          obj.brandly.blockId = undefined;
        }
      });
      this.canvasState.blocks[blockIdToDelete] = undefined;
      this.updateCanvasState();
      this.commitModification();
      this.setBorders();
    },
    exit() {
      const activeObject = this.fabricCanvas.getActiveObject();
      if (activeObject) {
        if (activeObject.type === 'IText') {
          activeObject.saveNow = true; //save immediate instead of debouncing
          activeObject.exitEditing(); //blur of text forces save
        }
      }
    },
    deleteField(options){
      const activeObject = this.fabricCanvas.getActiveObject();
      if (activeObject){

        if (activeObject.type === 'IText') {
          activeObject.exitEditing();
        }

        this.adjustObjectsAfterDeletion(activeObject);

        this.fabricCanvas.remove(activeObject);


        this.fabricCanvas.renderAll();

        this.commitModification();
      }
    },
    toggleDisplayBleed() {
      this.forceDisplayBleed = !this.displayBleed;
    },
    imageDataUploadedEvent(data){
      this.imageDataUploaded(data);
    },

    dismissBleedWarning(){
      // this.brandlyMetadata = this.brandlyMetadata || {};
      // this.brandlyMetadata.userDismissedBleedWarning = true;
      this.canvasState.dismissedBleedWarning = true;
    },
    duplicateFieldOrAddText(){
      const activeObject = this.fabricCanvas.getActiveObject();

      if (activeObject){
        const isText = activeObject.type === 'IText';
        if (isText){
          activeObject.exitEditing();
        }

        const height = activeObject.height;
        const object = fabric.util.object.clone(this.fabricCanvas.getActiveObject());

        // init cloned IText: initBehavior required to initialize events
        // https://github.com/fabricjs/fabric.js/issues/2154
        if (object.initBehavior){
          object.initBehavior();
        }

        const originalTop = object.top;

        if (isText){
          object.top = (originalTop + height + 5);
          object.left = (object.left);
        }
        else {
          object.top = (originalTop + 10);
          object.left = (object.left + 10);
        }

        this.fabricCanvas.add(object);

        if (isText && this.intersectsWithAnyObject(object, activeObject)){
          //try putting the text above.
          object.top = (originalTop - height - 5);
        }

        this.fabricCanvas.setActiveObject(object);

      }
      else{
        const text = 'new text';
        const textConstructor = {
          type: 'IText'
        };


        let fontFamily = 'Arial';
        if (this.canvasState.fonts && this.canvasState.fonts[0]){
          fontFamily = this.canvasState.fonts[0];
        }

        let fill = "rgb(237,28,36)";
        if (this.canvasState.colors && this.canvasState.colors[0]){
          fill = this.canvasState.colors;
        }

        const styleTemplate = {
          hasRotatingPoint:false,
          fontSize:12 * RATIO,
          fontFamily,
          fill
        };

        const styles = {};
        for (let i=0; i<text.length + 1; i++){
          styles[`${i}`] = JSON.parse(JSON.stringify(styleTemplate));
        }

        //not sure why, but "styles" is actually a one element array
        textConstructor.styles = [];
        textConstructor.styles.push(styles);
        textConstructor.fontSize = styleTemplate.fontSize;
        textConstructor.fontFamily = styleTemplate.fontFamily;
        textConstructor.fill = styleTemplate.fill;
        const textObj = new fabric.IText(text, textConstructor);

        const textPos = centerRectWithinBounds(
          {width:textObj.width, height:textObj.height},
          {width:this.fabricCanvas.getWidth(), height:this.fabricCanvas.getHeight()}
          );

        textObj.left = (textPos.left);
        textObj.top = (textPos.top);

        this.fabricCanvas.add(textObj);

      }

      this.fabricCanvas.renderAll();
      this.undoStack.push();
    },
    fontSize(value) {
      this.updateStyles({fontSize: value * RATIO});
    },
    fontName(value) {
      this.updateStyles({fontFamily: value});
    },
    color(value) {
      this.updateStyles({fill: value});
    },
    onTextMode(){
      this.textMode = true;
      this.refreshCursors();
      if (this.currentObject && this.currentObject.type === 'IText'){
        this.currentObject.enterEditing();
      }
    },
    onMoveMode(){
      this.textMode = false;
      this.refreshCursors();
      if (this.currentObject && this.currentObject.type === 'IText'){
        this.currentObject.exitEditing();
        this.fabricCanvas.setActiveObject(this.currentObject);
      }
    },

    setOriginX(newValue) {
      let obj = this.currentObject;
      // Ignore operation on non-texts
      if (!obj || obj.type !== 'IText') return;
      // No change needed, avoid extra work. This will frequently happen because
      // of the 2-way binding in the toolbar control
      if (obj.originX === newValue) return;
      // Set the origin. This shifts the object, adjust the "left" by
      // the amount of the shift so the position stays the same.
      let x = obj.aCoords.bl.x;
      obj.originX = newValue;
      obj.textAlign = newValue;
      obj.setCoords();
      let newX = obj.aCoords.bl.x;
      obj.left = obj.left + x - newX;
      obj.setCoords();
      this.updateCanvasState();
      this.fabricCanvas.renderAll();
      this.save();
    },

    setPosition({x, y}) {
      let obj = this.currentObject;
      obj.top = y;
      obj.left = x;
      obj.setCoords();
      this.updateCanvasState();
      this.fabricCanvas.renderAll();
      this.save();
    },

    makePlaceholder(){
      this.updateCustomProperties(obj => obj.isPlaceholder = !obj.isPlaceholder, 'image');
      this.fabricCanvas.trigger('object:modified', {target: this.fabricCanvas.getActiveObject()});
      this.commitModification();
    },

    setVariable(value) {
      this.updateCustomProperties(obj => obj.variable = value);
    },

    setAllowMultiline(value) {
      this.updateCustomProperties(obj => {
        obj.allowMultiline = value;
      }, 'IText');
    },

    setTextTransform(value) {
      this.updateCustomProperties(obj => {
        obj.textTransform = value;
      }, 'IText');
    },

    setFieldLock(value) {
      this.updateCustomProperties((brandly, obj) => {
        brandly.fieldLocked = value;
        obj.editable = !value;
      }, 'IText');
    },

    setFieldDeletable(value) {
      this.updateCustomProperties((brandly, obj) => {
        brandly.fieldDeletable = value;
      });
    },

    setBlockVerticalAlign(value) {
      let block = this.canvasState.blocks[this.canvasState.blockId];
      if (!block || typeof block == 'string') {
        //temp TODO remove
        this.canvasState.blocks[this.canvasState.blockId] = block = {};
      }
      block.verticalAlign = value;
      this.commitModification();
    },

    nextObject() {
      this.selectPrevOrNextField(1);
    },

    prevObject() {
      this.selectPrevOrNextField(-1);
    },

    setSuperscript() {
      if (this.currentObject && this.currentObject.type == 'IText') {
        let {selectionStart, selectionEnd} = this.currentObject;
        if (selectionStart != null && selectionEnd != null) {
          this.currentObject.setSuperscript(selectionStart, selectionEnd);
          this.fabricCanvas.renderAll();
        }
      }
    }

  },
  beforeDestroy() {
    this.saveNow();
  },
  methods: {
    ...mapActions(['setThumbnail', 'updateCanvasLayer', 'updatePage']),
    async loadPage() {
      console.log('loadPage');
      this.calculateFontsAndColors();
      var objects = this.page.textLayer.layerInfo;
      this.scaler = new Scaler(RATIO, RATIO);
      this.fabricCanvas = new fabric.Canvas(this.$refs.canvasEl, {
        renderOnAddRemove: false,
        backgroundColor: null,
        enableRetinaScaling: true,
        objectCaching: false,
        imageSmoothingEnabled: false
      });
      console.log("mediaBox:", this.page.mediaBox);
      let [width, height] = this.scaler.canvasSize(this.page.mediaBox);
      let [trimBoxWidth, trimBoxHeight] = this.scaler.canvasSize(this.page.trimBox);
      [this.trimHorizontalMargins, this.trimVerticalMargins] = this.scaler.trimSize(this.page.trimBox);

      // Business cards are 3.5" by 2" (or 2" by 3.5"). If the file's trimbox is greater than this,
      // then it was set up without a proper cropbox, and we assume there should be one here. Note that
      // numbers are calculated based on the size of the canvas, which is at 200% (eg, 3.5" * 72ppi * 2 = 504px).
      const isLandscape = trimBoxWidth > trimBoxHeight;
      const MAX_TRIMBOX_WIDTH = 72 * (isLandscape ? 3.5 : 2.0) * 2;
      const MAX_TRIMBOX_HEIGHT = 72 * (isLandscape ? 2.0 : 3.5) * 2;

      if (trimBoxWidth > MAX_TRIMBOX_WIDTH + 1){ //plus 1 to account for any rounding errors
        this.trimHorizontalMargins = (width - MAX_TRIMBOX_WIDTH) / 2
      }
      if (trimBoxHeight > MAX_TRIMBOX_HEIGHT + 1){ //plus 1 to account for any rounding errors
        this.trimVerticalMargins = (height - MAX_TRIMBOX_HEIGHT) / 2;
      }

      this.fabricCanvas.setWidth(width);
      this.fabricCanvas.setHeight(height);

      // RCB: removed await Promise.all() here because loading order seems dependent on loadScaledObjects happening first
      // potentially bring it back if this slows things down too much.
      await this.scaler.loadScaledObjects(this.fabricCanvas, objects);
      if (objects.meta && objects.meta.dismissedBleedWarning){
        this.canvasState.dismissedBleedWarning = true;
      }
      await this.loadBackgroundImage();
      this.loadMeta();
      this.setBorders();
      this.undoStack = new UndoStack(this.fabricCanvas, 50, this.canvasState);
      this.undoStack.push();

      this.templateVars = new TemplateVars(this.fabricCanvas, this.$store);
      if (!this.design.isTemplate && !this.nofill) {
        this.templateVars.replaceAll();
        this.deleteEmptyFields();
      }

      this.fabricCanvas.renderAll();

      this.assignBleedPoints();

      this.checkForText();

      this.refreshCursors();
      this.lockObjects();
      this.updateThumbnail();
      this.bindCanvasEvents();

      initAligningGuidelines(this.fabricCanvas);
      selectTopLeftObject(this.fabricCanvas);
    },

    placeholderImageDataUploaded(data){
      this.imageDataUploaded(data, true);
    },

    refreshCursors(){
      this.fabricCanvas.forEachObject(obj =>{
        if (this.designLocked) {
          obj.hoverCursor = 'default';
        } else if (obj.type === 'IText'){
          obj.hoverCursor = this.textMode ? 'text' : 'move';
        }
      });
    },

    lockObjects() {
      if (this.designLocked) {
        console.log('locking!')
        this.fabricCanvas.forEachObject(obj => {
          obj.lockMovementX = true;
          obj.lockMovementY = true;
          obj.lockScalingX = true;
          obj.lockScalingY = true;
          obj.lockUniScaling = true;
          obj.hasControls = false;
        })
      }
    },

    // call this after an operation has completed.
    // adds a save
    commitModification(saveNow){
      if (saveNow){ //saveNow if this was result of checkout being clicked.
        this.saveNow()
      }
      else{ //normal case, debounce the save
        this.save();
      }
      this.undoStack.push();
      this.assignBleedPoints(); //getBleedPoints(this.fabricCanvas, this.$refs.canvasEl.getContext('2d'), this.trimHorizontalMargins, this.trimVerticalMargins);
    },

    assignBleedPoints(){
      this.bleedPoints = getBleedPoints(this.fabricCanvas, this.$refs.canvasEl.getContext('2d'), this.trimHorizontalMargins, this.trimVerticalMargins, BLEED_POINT_THRESHOLD);
      this.canvasState.hasBadBleed = anyBadBleed(this.bleedPoints);
    },

    checkForText(){
      let hasText = false;
      this.fabricCanvas.forEachObject(obj =>{
        if (obj.type === 'IText'){
          hasText = true;
          console.log('found text')
        }
      });
      this.canvasState.hasTextAtStartup = hasText;
    },

    imageDataUploaded(data, replacePlaceholder) {
      const activeObject = this.fabricCanvas.getActiveObject();

      let bounds, maxSize, toRemove;

      bounds = {left: 0, top: 0, width: this.fabricCanvas.getWidth(), height: this.fabricCanvas.getHeight()};
      maxSize = {width: 250, height: 250};

      if (activeObject) {
        if (activeObject.type === 'IText') {
          activeObject.exitEditing();
        }
        // Handle case where we click the upload while placeholder is selected
        // TODO remove replacePlaceholder? why added?
        if (replacePlaceholder) {
          // Upload into this spot
          let top = activeObject.top, left = activeObject.left;
          let width = activeObject.getScaledWidth(), height = activeObject.getScaledHeight();
          bounds = {top, left, width, height};
          maxSize = {width, height};
          toRemove = activeObject;
        }
      }

      // Create new image
      fabric.Image.fromURL(data, (img) => {
        let newDim = {width: img.width, height: img.height};

        if (newDim.width > maxSize.width || newDim.height > maxSize.height) {
          newDim = fitRectIntoBounds(newDim, maxSize);
        }

        const newPos = centerRectWithinBounds(newDim, bounds);

        img.scaleToWidth(newDim.width);
        img.set({left: newPos.left, top: newPos.top, angle: 0});

        this.fabricCanvas.add(img).setActiveObject(img);



        if (toRemove){
          // this code would replace a placeholder with a new placeholder.
          // sounds like that's not exactly what we want.
          // this.updateCustomProperties(obj => obj.isPlaceholder = true, 'image', true);

          this.fabricCanvas.remove(toRemove);
        }
        this.fabricCanvas.renderAll();

        //this "reminds" the placeholder icon to turn on
        this.fabricCanvas.trigger('object:modified', {target: this.fabricCanvas.getActiveObject()});

        this.commitModification();
      })
    },

    calculateFontsAndColors() {
      // Update state with fonts and colors from design
      this.canvasState.fonts = uniq(flatten(this.design.fonts));
      this.canvasState.colors = this.design.colors.map(x => `rgb(${x.rgb})`);
    },
    async loadBackgroundImage() {
      return new Promise(resolve => {
        fabric.Image.fromURL(this.backgroundSvgDataUrl, (img) => {
          img.set({width: this.fabricCanvas.width, height: this.fabricCanvas.height});
          this.fabricCanvas.setBackgroundImage(img, () => {
            resolve();
          });
        });
      });
    },

    loadMeta() {
      let {blocks} = this.page.textLayer.layerInfo.meta||{};
      this.canvasState.blocks = blocks||{};
    },

    // adds the custom properties on the active object
    updateCustomProperties(updateFunc, validObjectType, skipCommitAndRender) {
      if (this.currentObject && (!validObjectType || this.currentObject.type === validObjectType)) {
        this.currentObject.brandly = this.currentObject.brandly || {};
        updateFunc(this.currentObject.brandly, this.currentObject);
        this.setBorder(this.currentObject);
        this.updateCanvasState();

        if (!skipCommitAndRender){
          this.commitModification();
          this.fabricCanvas.renderAll();
        }
      }
    },

    // adds the custom properties on an array of objects
    updateCustomPropertiesOnMultipleObjects(objArray, updateFunc, validObjectType){
      objArray.forEach(obj => {
        if (obj && (!validObjectType || obj.type === validObjectType)) {

          // console.log('adding block thingy')
          obj.brandly = obj.brandly || {};
          updateFunc(obj.brandly);
          this.setBorder(obj);
        }
      })
      this.updateCanvasState();
      this.commitModification();
      this.fabricCanvas.renderAll();

    },

    setBorders() {
      // Ensure all placeholder objects have their borders set
      this.fabricCanvas.getObjects().map(obj => this.setBorder(obj));
      this.fabricCanvas.renderAll();
    },

    setBorder(object) {
      // Set the color/controls for an image based on whether its a placeholder or in a group
      // Caller should renderAll()
      const STANDARD_BORDER_COLORS = ['rgba(102,153,255,0.75)', 'rgba(102,255,153,0.75)', 'rgba(153,102,255,0.75)'];
      const PLACEHOLDER_BORDER_COLORS = ['rgba(0,255,0, 0.75)', 'rgba(255,0,0, 0.75)', 'rgba(0,0,255, 0.75)'];
      const {isPlaceholder, blockId} = (object.brandly || {});
      const colorIx = (blockId || 0) % STANDARD_BORDER_COLORS.length;
      object.borderColor = isPlaceholder ? PLACEHOLDER_BORDER_COLORS[colorIx] : STANDARD_BORDER_COLORS[colorIx];
      console.log('setBorder', object.borderColor, colorIx);
      if (object.type !== 'image') return;
      ['tl', 'ml', 'bl', 'mb', 'br', 'mr', 'tr', 'mt', 'mtr'].forEach(control => {
        object.setControlVisible(control, !isPlaceholder);
      });
    },

    intersectsWithAnyObject(obj, except){
      for (const canvasObject of this.fabricCanvas.getObjects()){
        if (canvasObject.type === 'IText' &&
          obj !== canvasObject &&
          canvasObject !== except){
          if (obj.intersectsWithObject(canvasObject)){
            console.log('intersects with', canvasObject);
            return true;
          }
        }
      }
      return false;
    },

    onDeselected(event) {
      // When leaving a field that is blank, delete it
      if (event.deselected && event.deselected.length === 1) {
        this.deleteIfEmpty(event.deselected);
      }
    },

    deleteIfEmpty(field) {
      if (field.type === 'IText' && field.text === '') {
        this.adjustObjectsAfterDeletion(field);
        this.fabricCanvas.remove(field);
      }
    },

    deleteEmptyFields() {
      this.fabricCanvas.getObjects().map(field => this.deleteIfEmpty(field));
      this.fabricCanvas.renderAll();
    },

    onEditingEntered(event) {
      const object = event.target;
      console.log('onEditingEntered', object);
      if (object.type === 'IText'){
        let {fieldLocked} = object.brandly || {};
        if (fieldLocked) object.exitEditing();
      }
    },

    onSelectionChanged(event){
      const object = event.target;

      if (object.type === 'IText'){
        let {fieldLocked} = object.brandly || {};
        object.hasControls = false;

        // if(object.brandly && object.brandly.blockId){
        //   const textObjectsInBlock = this.fabricCanvas.getObjects().filter(filterObj => {
        //     return filterObj.brandly && filterObj.brandly.blockId === object.brandly.blockId;
        //   });
        // }

        if (!fieldLocked && (this.textMode || this.designLocked)) {

          // As soon as the object is selected, enter editing mode. This prevents movement, and is
          // an easier way to get into the editing mode.
          let object = event.target;
          object.enterEditing();


          const mouseEvent = event ? event.e : null;

          // if not a mouse event means that this likely came from "selectTopLeftObject" or something else that
          // doesn't know where to set the cursor based on the pointer.
          if (!!mouseEvent){
            const selectionIdx = object.getSelectionStartFromPointer(event);

            //this is wierd, I couldn't get the cursor to start w/ object.startCursorAnimation,
            //but after trying a bunch of things, this seems to work :-/
            object.setSelectionStart(selectionIdx);
            this.$nextTick(() =>{
              object.setSelectionEnd(selectionIdx);
              this.updateCanvasState();
            });
          }

          this.currentObject = object;
        }
      }

      this.currentObject = object;
      this.updateCanvasState();
    },
    bindCanvasEvents() {
      this.fabricCanvas.on({
        'object:modified': (event) => {
          let saveNow = false;
          // if current object is decorated w/ saveNow, force save immediately.
          // used if checkout button is clicked while editing text.
          if (event.target && event.target.saveNow){
            delete event.target.saveNow;
            saveNow = true;
          }
          this.commitModification(saveNow);
        },
        'selection:updated': (event) => {
          this.onDeselected(event);
          this.onSelectionChanged(event)
        },
        'selection:created': (event) => {
          this.onSelectionChanged(event)
        },
        'selection:cleared': (event) => {
          this.onDeselected(event);
          if (this.currentObject) {
            this.currentObject = null;
          }
        },
        'mouse:over': (event) => {
          if (!this.fabricCanvas.getActiveObject() && event.target) {
            const object = event.target;
            object.set('active', true);
            if (object.type === 'IText'){
              object.hasControls = false;
            }
            this.fabricCanvas.renderAll();
          }
        },
        'mouse:out': (event) => {
          if (!this.fabricCanvas.getActiveObject() && event.target) {
            event.target.set('active', false);
            this.fabricCanvas.renderAll();
          }
        },
        'text:changed': (event) => {
          let object = this.fabricCanvas.getActiveObject();
          if (!object || object.type !== 'IText') return;
          let {allowMultiline, textTransform} = object.brandly || {};
          let text = object.text, newText = text;
          if (!allowMultiline) {
            newText = text.replace(/\n/gm, ""); //Remove line breaks
            newText = newText.replace('\t', ' '); // Replace tabs with spaces
          }
          if (textTransform) {
            // Errors if textTransform not found
            let func = {
              uppercase(text) {
                // return text.toUpperCase();
                // Fancy uppercasing that allows McDONALD, van ROSSUM, van der BEEK
                return text.replace(/M(a?c)|( va?n?)|( de?r?)|./g,
                  (m, ac, van, der) => (ac||van||der) ? m : m.toUpperCase());
              }
            }[textTransform] || (text => text);
            newText = func(newText);
          }

          if (text !== newText) {
            object.set({text: newText});
            object.moveCursorWithoutShift(newText.length - text.length); // Compensate for number of characters removed
            this.fabricCanvas.renderAll();
          }
        },
        'text:selection:changed': (event) => {
          if (this.currentObject){
            this.updateCanvasState();
          }
        },
        'text:editing:entered': (event) => {
          this.onEditingEntered(event);
        },


        //TODO -- this was implemented in `spa` (canvas.service.ts). Figure out need and incorporate
        // 'text:selection:changed': debounce((event) => {
        //       let target = event.target;
        //       this.currentObject = target;

        //       if (target.selectionEnd === target.selectionStart) {
        //           let textBeforeEdit = target._textBeforeEdit;

        //           // User about to enter text (make sure we have the right style)
        //           if (target.text == target._textBeforeEdit) {
        //               let previousChar = target.text[target.selectionStart-1];
        //               let nextChar = target.text[target.selectionStart];

        //               // Fabric for some reason defaults to previousChar style by default (we need to override)
        //               if (previousChar === " " && nextChar !== " ") {
        //                   let style = target.styles[0][target.selectionStart];
        //                   target.styles[0][target.selectionStart-1] = style;
        //                   this.persistChange();
        //               }

        //           }
        //       }
        //   }, 300),
      });
    },
    hideBorders(block) {
      // Ensure we hide any current text editing controls
      let object = this.fabricCanvas.getActiveObject();
      if (!object || (object.type !== 'IText' && object.type !== 'image') || !object.hasBorders) {
        // No need to hide borders
        block();
        return
      }
      object.hasBorders = false;
      let orig = object.cornerSize;
      object.cornerSize = 0;
      this.fabricCanvas.renderAll();

      block();

      object.hasBorders = true;
      object.cornerSize = orig;
      this.fabricCanvas.renderAll();
    },
    updateThumbnail() {
      this.hideBorders(() => {
        const dataUrl = cropCanvas(this.$refs.canvasEl, this.trimHorizontalMargins, this.trimVerticalMargins).toDataURL();
        this.setThumbnail({pageNum: this.pageNum, dataUrl});
        this.saveCanvasSvg(dataUrl);
      });
    },
    saveNow() {
      if (!this.fabricCanvas) {
        console.error("saveNow: page hasn't loaded");
        return;
      }
      this.saveLayerObjects();
      this.updateThumbnail();
    },
    save: debounce(function () {
      this.saveNow();
    }, 300),
    updateStyles(styles) {
      if (this.currentObject.type === 'IText'){
        this.currentObject.setSelectionStyles(styles);
      }
      this.fabricCanvas.renderAll();
      this.updateCanvasState();

      this.commitModification();
    },

    getTextStyleFromSelection(textObject){
      let styleStart = textObject.selectionStart;
      let styleEnd = textObject.selectionEnd;


      if (styleStart === styleEnd && textObject.text.length > 0){
        //if we're at the beginning, get the style of the first char in string
        if (styleStart === 0){
          styleEnd = 1;
        }
        //else get the style of the previous char
        else{
          styleStart -= 1;
        }
      }
      const styles = textObject.getSelectionStyles(styleStart, styleEnd, true);
      return styles[0] || null;

    },
    updateCanvasState() {
      let canvasState = this.canvasState, object = this.currentObject;
      canvasState.displayBleed = this.displayBleed;
      if (!object) {
        canvasState.fontName = undefined;
        canvasState.fontSize = undefined;
        canvasState.color = undefined;
        canvasState.originX = undefined;
        canvasState.variable = undefined;
        canvasState.isPlaceholder = undefined;
        canvasState.allowMultiline = undefined;
        canvasState.textTransform = undefined;
        canvasState.textSelected = false;
        canvasState.fieldLocked = undefined;
        canvasState.fieldDeletable = undefined;
        canvasState.blockId = undefined;
        canvasState.position = {x:null, y:null};
        return;
      }
      if (object.type === 'IText'){
        const style = this.getTextStyleFromSelection(object);

        if (style){
          canvasState.fontName = style.fontFamily;
          let fontSize = (style.fontSize || 12) / RATIO; // TODO port magic ratio crap
          if (Math.round(fontSize) !== fontSize ){
            fontSize = fontSize.toFixed(2);
          }
          canvasState.fontSize = fontSize;
          canvasState.color = style.fill;
        }
        canvasState.originX = object.originX;
      } else {
        canvasState.originX = undefined;
      }
      canvasState.position = {x: object.left, y: object.top};
      let brandly = object.brandly || {};
      canvasState.variable = brandly.variable || '';
      canvasState.isPlaceholder = brandly.isPlaceholder;
      canvasState.allowMultiline = brandly.allowMultiline;
      canvasState.textTransform = brandly.textTransform;
      canvasState.fieldLocked = brandly.fieldLocked;
      canvasState.fieldDeletable = brandly.fieldDeletable;
      canvasState.blockId = brandly.blockId;
      canvasState.textSelected = object.type === 'IText';
    },
    saveLayerObjects() {
      let pageNum = this.pageNum;

      let bg = this.fabricCanvas.backgroundImage;
      this.fabricCanvas.setBackgroundImage(null);
      let objects = this.scaler.unscaledObjects(this.fabricCanvas);
      this.fabricCanvas.setBackgroundImage(bg);

      const meta = {
        dismissedBleedWarning:this.canvasState.dismissedBleedWarning
      }
      meta.blocks = this.canvasState.blocks;

      this.updateCanvasLayer({pageNum, objects, meta});
    },
    async saveCanvasSvg(previewDataUrl) {
      let pageNum = this.pageNum;

      // Create new canvas, and load with unscaled objects
      let el = document.createElement('canvas');
      let canvas = new fabric.Canvas(el);
      let objects = this.scaler.unscaledObjects(this.fabricCanvas).filter(notPlaceholders);
      console.log(objects);
      canvas.loadFromJSON(JSON.stringify({objects}));
      canvas.renderAll();
      let [x, y, width, height] = this.page.mediaBox;

      // Without this, images won't appear in output
      // TODO remove timeouts / understand why needed
      await new Promise(r => setTimeout(r, 1));

      let svg = canvas.toSVG({
            width, height, viewBox: {x, y, width, height}
      });

      this.updatePage({pageNum, svg, previewDataUrl});

      function notPlaceholders({brandly}) {
        if (brandly && brandly.isPlaceholder) {
          console.log('saveCanvasSvg: isPlaceholder!');
        }
        return !(brandly && brandly.isPlaceholder);
      }
    },
    selectPrevOrNextField(offset) {
      let cur = this.currentObject;
      let objects = this.fabricCanvas.getObjects();
      if (objects.length === 0) return;
      let obj;
      if (!cur) {
        obj = objects[0];
      } else {
        let index = objects.indexOf(cur);
        console.log('cur index', index);
        index = (index + objects.length + offset) % objects.length;
        console.log('new index', index);
        obj = objects[index];
      }
      console.log('new object', obj);
      this.fabricCanvas.setActiveObject(obj);
    },
    adjustObjectsAfterDeletion(deletedObject) {
      // Adjust other objects in the same block based on vertical aligment.
      // Caller should renderAll.
      let {blockId} = deletedObject.brandly||{};
      if (!blockId) return; // Most objects won't have a block
      let {verticalAlign} = this.canvasState.blocks[blockId]||{};
      if (!verticalAlign) {
        // If an object has a block it should have a vertical alignment
        console.error('Block missing vertical align', blockId);
        return;
      }

      // Find the other objects, in two lists (above, and below)
      // Keep the closest object at front of list
      let r = deletedObject.getBoundingRect();
      let above = [], below = [];
      for (let obj of this.fabricCanvas.getObjects()) {
        if (!Object.is(obj, deletedObject) && obj.brandly && obj.brandly.blockId == blockId) {
          let r2 = obj.getBoundingRect();
          if (overlapsX(r, r2)) {
            let list = r.top > r2.top ? above : below;
            let gapIsSmaller = list.length && gap(r, r2) < gap(r, list[0]);
            list[gapIsSmaller ? 'unshift' : 'push'](obj);
          }
        }
      }


      // Calculate how much to move objects above and below, respectively
      // We move objects up or down so that the object above or below
      // would be in the same position after the move. If its centered,
      // then moving half works fine.
      // This is fine when all objects have a consistent gap, but when
      // line might have larger gaps we might need to tweak this.
      let moveDown = 0, moveUp = 0;
      if (verticalAlign === 'top') {
        moveUp = below.length && below[0].top - r.top;
      } else if (verticalAlign === 'bottom') {
        moveDown = above.length && (r.top + r.height) - (above[0].top + above[0].height);
      } else if (verticalAlign === 'middle') {
        moveUp = below.length && (below[0].top - r.top) / 2;
        moveDown = above.length && ((r.top + r.height) - (above[0].top + above[0].height)) / 2;
      }

      // Move
      above.forEach(obj => {obj.top += moveDown; obj.setCoords(); });
      below.forEach(obj => {obj.top -= moveUp; obj.setCoords(); });

      function gap(r1, r2) {
        // Gap between objects, vertically
        return r1.top < r2.top ? (r2.top - r1.top + r1.height) : (r1.top - r2.top + r2.height);
      }
      function overlapsX(r1, r2) {
        return (r2.left <= (r1.left + r1.width)) && ((r2.left + r2.width) >= r1.left);
      }

    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/pages/editor/EditorCanvas.vue